import type {
  AppendixConsistencyReport,
  AppendixConsistencyResult,
  AppendixFinding,
  AppendixSlotPattern,
  UnselectableRuleLine,
} from '../types/codeIndex'

// Helpers for the comparison of the rules against appendices that define valid combinations.

export function formatOutstandingUnselectableMessage(count: number): string {
  return `${count} value${count === 1 ? '' : 's'} cannot be selected. Resolve ${count === 1 ? 'it' : 'them'} before submitting.`
}

export function formatOutstandingDisagreementMessage(count: number): string {
  return `${count} disagreement${count === 1 ? '' : 's'} between the rules and an appendix. Resolve ${count === 1 ? 'it' : 'them'} before submitting.`
}

// What stands between the project and review, telling apart the two checks that share the gate.
export function formatOutstandingReviewMessage(unselectableCount: number, disagreementCount: number): string {
  if (disagreementCount === 0) return formatOutstandingUnselectableMessage(unselectableCount)
  if (unselectableCount === 0) return formatOutstandingDisagreementMessage(disagreementCount)
  const total = unselectableCount + disagreementCount
  return `${total} items need attention before submitting: ${unselectableCount} value${unselectableCount === 1 ? '' : 's'} `
    + `cannot be selected, and ${disagreementCount} disagreement${disagreementCount === 1 ? '' : 's'} between the rules and an appendix.`
}

export function formatCodeReference(code: string): string {
  return `Code ${code}`
}

function formatSlotLabel(slot: Pick<AppendixSlotPattern, 'codeItemCode' | 'valuePosition' | 'valuePositionEnd'>): string {
  const reference = formatCodeReference(slot.codeItemCode)
  if (slot.valuePosition === null || slot.valuePosition === undefined) return reference
  const end = slot.valuePositionEnd ?? slot.valuePosition
  return slot.valuePosition === end
    ? `${reference} pos ${slot.valuePosition + 1}`
    : `${reference} pos ${slot.valuePosition + 1}-${end + 1}`
}

// "Code 018 pos 1 = A · Code 018 pos 2 any · Code 018 pos 4 = C, D"
export function formatSlotPattern(pattern: AppendixSlotPattern[]): string {
  return pattern
    .map(slot => slot.values.length === 0
      ? `${formatSlotLabel(slot)} any`
      : `${formatSlotLabel(slot)} = ${slot.values.join(', ')}`)
    .join(' · ')
}

export function formatCombinationCount(count: number): string {
  return `${count} combination${count === 1 ? '' : 's'}`
}

export function formatDisagreementCount(count: number): string {
  return count === 1
    ? '1 place where the rules and the table disagree.'
    : `${count} places where the rules and the table disagree.`
}

export function getConsistencyResult(
  report: AppendixConsistencyReport | null | undefined,
  appendixId: string | null | undefined
): AppendixConsistencyResult | null {
  if (!report || !appendixId) return null
  return report.appendices.find(result => result.appendixId === appendixId) ?? null
}

// Findings attached to a row of the table: rows the rules reject (or that name no value).
export function getRowFindings(result: AppendixConsistencyResult | null | undefined): Map<string, AppendixFinding> {
  const byRow = new Map<string, AppendixFinding>()
  result?.findings.forEach(finding => {
    if (finding.kind === 'RejectedRow' && finding.rowId) byRow.set(finding.rowId, finding)
  })
  return byRow
}

// Findings with no row to attach to: omitted combinations, the no-rules case and unmapped columns.
export function getTableFindings(result: AppendixConsistencyResult | null | undefined): AppendixFinding[] {
  return (result?.findings ?? []).filter(finding => finding.kind !== 'RejectedRow')
}

// Draft rows for the combinations of a finding, keyed by the draft column each slot names: by saved
// column id, so columns moved since the comparison still get the right values. A slot whose column
// is gone, or a report too old to carry ids, yields null: the table no longer matches the finding.
export function buildRowsForFinding(
  finding: AppendixFinding,
  columns: Array<{ key: string; id?: string | null }>
): Array<Record<string, string>> | null {
  const columnKeys: string[] = []
  for (const slot of finding.pattern) {
    const column = slot.columnId ? columns.find(candidate => candidate.id === slot.columnId) : undefined
    if (!column) return null
    columnKeys.push(column.key)
  }

  return finding.combinations.map(combination => {
    const cells: Record<string, string> = {}
    columnKeys.forEach((columnKey, index) => {
      if (combination[index] !== undefined) cells[columnKey] = combination[index]
    })
    return cells
  })
}

export type RuleDisagreement = {
  appendixId: string
  letter: string
  title: string
  combinationCount: number
}

// Every rule a finding names, keyed by the format value the rule lives on, with how many
// combinations of which appendix it takes part in rejecting.
export function getRuleDisagreements(report: AppendixConsistencyReport | null | undefined): Map<string, RuleDisagreement[]> {
  const byFormatValue = new Map<string, RuleDisagreement[]>()
  report?.appendices.forEach(result => {
    const counts = new Map<string, { line: UnselectableRuleLine; count: number }>()
    result.findings.forEach(finding => {
      finding.rules.forEach(line => {
        if (!line.formatValueId) return
        const current = counts.get(line.formatValueId)
        counts.set(line.formatValueId, { line, count: (current?.count ?? 0) + finding.combinationCount })
      })
    })
    counts.forEach(({ count }, formatValueId) => {
      const entries = byFormatValue.get(formatValueId) ?? []
      entries.push({ appendixId: result.appendixId, letter: result.letter, title: result.title, combinationCount: count })
      byFormatValue.set(formatValueId, entries)
    })
  })
  return byFormatValue
}

// The rule lines and covered code items of every outstanding finding: what has to change before a
// recheck can tell anything new.
export function getImplicatedCodeItems(report: AppendixConsistencyReport | null | undefined): Set<string> {
  const codes = new Set<string>()
  report?.appendices.forEach(result => {
    if (result.findings.length === 0) return
    result.codeItemCodes.forEach(code => codes.add(code))
    result.findings.forEach(finding => finding.rules.forEach(line => codes.add(line.codeItemCode)))
  })
  return codes
}

export function describeAppendix(result: Pick<AppendixConsistencyResult, 'letter' | 'title'>): string {
  return result.letter ? `Appendix ${result.letter}` : result.title
}

// "Appendix C: 4 disagreements with the rules."
export function formatAppendixDisagreementLine(result: AppendixConsistencyResult): string {
  const count = result.findings.length
  return `${describeAppendix(result)}: ${count} disagreement${count === 1 ? '' : 's'} with the rules.`
}
